import { Button, PinInput } from '@mantine/core';
import { useInterval } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { AuthTokenResponse, mfaSend, mfaVerify, parseApiErrorCode } from '@mygpt/app';

const CODE_LENGTH = 6;
const DEFAULT_RESEND_INTERVAL = 60;

interface MfaVerificationProps {
  /** authorize / google_verify 返回的一次性挑战 token（10 分钟有效） */
  mfaToken: string;
  /** 掩码后的邮箱，用于提示验证码发送位置 */
  emailMasked: string;
  /** 初始重发冷却秒数（首条验证码已随 authorize 响应自动发出） */
  resendAfter: number;
  /** 验证成功：响应与 authorize 成功同构，由父组件复用登录完成逻辑 */
  onSuccess: (data: AuthTokenResponse) => void;
  /** 返回登录第一步（用户主动返回） */
  onBack: () => void;
  /** 挑战会话失效（MFA_TOKEN_EXPIRED / 用户已删除），回登录第一步并展示提示 */
  onSessionExpired: (message: string) => void;
}

/**
 * 登录二步验证（邮箱验证码）
 * 契约 §1.4 /auth/mfa/verify、§1.5 /auth/mfa/send
 */
const MfaVerification = ({
  mfaToken,
  emailMasked,
  resendAfter,
  onSuccess,
  onBack,
  onSessionExpired,
}: MfaVerificationProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // 重发倒计时：初始用 authorize 返回的 resend_after，之后按默认 60s / 429 的 retry_after
  const [seconds, setSeconds] = useState(resendAfter > 0 ? resendAfter : 0);
  const interval = useInterval(() => setSeconds((s) => s - 1), 1000);

  useEffect(() => {
    if (resendAfter > 0) {
      interval.start();
    }
    return interval.stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (seconds <= 0) {
      interval.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);

  const restartCountdown = (nextSeconds: number) => {
    setSeconds(nextSeconds);
    if (nextSeconds > 0) {
      interval.start();
    }
  };

  const handleVerify = async (inputCode?: string) => {
    const targetCode = inputCode ?? code;
    if (targetCode.length !== CODE_LENGTH || verifying) return;

    setError('');
    setResendSuccess(false);
    setVerifying(true);
    try {
      const res = await mfaVerify({ mfa_token: mfaToken, code: targetCode });
      if (!res.data) return;
      onSuccess(res.data);
    } catch (err: unknown) {
      const codeDetail = parseApiErrorCode(err);
      setCode('');
      if (codeDetail?.code === 'MFA_CODE_INVALID') {
        if (typeof codeDetail.attempts_left === 'number') {
          setError(t('mfa.code_invalid', { count: codeDetail.attempts_left }));
        } else {
          setError(t('mfa.code_invalid_generic'));
        }
      } else if (codeDetail?.code === 'MFA_CODE_EXPIRED') {
        // 连续 5 次错 / 码已作废，引导点「重新发送」
        setError(t('mfa.code_expired'));
      } else if (codeDetail?.code === 'MFA_TOKEN_EXPIRED') {
        onSessionExpired(t('mfa.session_expired'));
      } else if ((err as { statusCode?: number })?.statusCode === 401) {
        // 极端情况（如用户已删除），回登录页
        onSessionExpired(t('mfa.session_expired'));
      } else {
        setError(t('mfa.verify_failed'));
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (seconds > 0 || resending) return;

    setError('');
    setResendSuccess(false);
    setResending(true);
    try {
      await mfaSend({ mfa_token: mfaToken });
      // 204 已重发（错误计数清零）
      setResendSuccess(true);
      setCode('');
      restartCountdown(resendAfter > 0 ? resendAfter : DEFAULT_RESEND_INTERVAL);
    } catch (err: unknown) {
      const codeDetail = parseApiErrorCode(err);
      if (codeDetail?.code === 'MFA_RESEND_COOLDOWN') {
        // 冷却中，用 retry_after 重置倒计时
        restartCountdown(codeDetail.retry_after ?? DEFAULT_RESEND_INTERVAL);
      } else if (codeDetail?.code === 'MFA_SEND_LIMIT') {
        setError(t('mfa.send_limit'));
      } else if (codeDetail?.code === 'MFA_TOKEN_EXPIRED') {
        onSessionExpired(t('mfa.session_expired'));
      } else {
        setError(t('mfa.verify_failed'));
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-6 bg-white py-6 px-4 md:py-8 md:px-6 shadow rounded-xl border border-gray-200 sm:px-10">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{t('mfa.title')}</h3>
        <p className="mt-2 text-sm text-gray-600">{t('mfa.description', { email: emailMasked })}</p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
      {resendSuccess && !error && (
        <div className="rounded-md bg-green-50 p-4">
          <p className="text-sm text-green-700">{t('mfa.resend_success')}</p>
        </div>
      )}

      <div className="flex justify-center">
        <PinInput
          length={CODE_LENGTH}
          type="number"
          oneTimeCode
          autoFocus
          size="lg"
          value={code}
          onChange={(value) => {
            setCode(value);
            // 仅在用户实际输入时清除错误提示。
            // 注意:验证失败后的 setCode('') 会触发 Mantine PinInput 内部
            // 对受控空值的同步(useUncontrolled → onChange(''))——若不加
            // value 判断,刚设置的错误提示会被这次回调立即清掉。
            if (error && value) setError('');
          }}
          onComplete={(value) => handleVerify(value)}
          disabled={verifying}
          aria-label={t('mfa.title')}
        />
      </div>

      <Button
        fullWidth
        size="md"
        radius="md"
        loading={verifying}
        loaderProps={{ size: 'sm', color: 'white' }}
        disabled={code.length !== CODE_LENGTH || verifying}
        onClick={() => handleVerify()}
        className="bg-gray-900 hover:bg-gray-800 text-white font-medium text-base md:text-sm py-3.5 md:py-3 shadow-sm transition-colors duration-200"
      >
        {t('mfa.verify')}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onBack}
          className="font-medium text-gray-600 hover:text-gray-800 transition-colors"
        >
          {t('mfa.back_to_login')}
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={seconds > 0 || resending}
          className="font-medium text-primary-600 hover:text-primary-500 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {seconds > 0 ? t('mfa.resend_countdown', { seconds }) : t('mfa.resend')}
        </button>
      </div>
    </div>
  );
};

export default MfaVerification;
